import React, { FunctionComponent, useEffect, useState } from "react";
import { connect } from "react-redux";
import styles from "./emicalculator.module.css";
import { themeColors } from "../../Components/theme";
import "../../theme-vars.css";
import Loading from "../../Components/Loading/Loading";
import InputText from "../AreaConverter/InputText";
import { Box, Grid, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import StyledSubmitButton from "../../Components/Buttons/StyledSubmitButton";

type ConversionItem = {
    unit: string;
    unitVal: number;
};
type props = {
    theme: any;
};

const EmiCalculator: FunctionComponent<props> = ({ theme }) => {
    const themeMUI = useTheme();
    const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
    const [showLoader, setShowLoader] = useState(true);
    const [emi, setEmi] = useState(0);
    const [principal, setPrincipal] = useState<number>(0);
    const [interest, setInterest] = useState<number>(0);
    const [tenure, setTenure] = useState<number>(0);
    const [total, setTotal] = useState(0);
    const [intPayable, setIntPayable] = useState(0);
    const [send, setSend] = useState<any>([0, 0]);

    function fixedFloat(nbr: any, toFixed = 2) {
        return parseFloat(nbr.toFixed(toFixed));
    }

    function calculateEMI() {
        let emi = 0;
        let r = interest;
        let p = principal;
        let n = tenure;

        if (r === 0 || p === 0 || n === 0) {
            setEmi(0);
        } else {
            r = interest / 12 / 100;
            emi = p * r * (Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1));
            setEmi(emi);

            let t = emi * n;
            setTotal(t);
            const interestAmount = t - p;
            setIntPayable(interestAmount);
            setSend([p, interestAmount]);
        }
    }

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowLoader(false);
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        setSend([principal, intPayable]);
    }, [principal, intPayable]);

    const containerStyles = {
        backgroundColor: theme
            ? themeColors.backgroundColor
            : themeColors.backgroundColorDark,
        color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
    };
    const cardContainerStyles = {
        backgroundColor: theme
            ? themeColors.cardBackgroundColor
            : themeColors.cardBackgroundColorDark,
        color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
    };
    const buttonColors = {
        backgroundColor: theme
            ? "#e6e6e6"
            : themeColors.backgroundColorDark,
        color: theme ? "#333" : themeColors.textPrimaryDark,
    };

    return (
        <>
            <Box
                className={styles.parent}
                sx={{
                    ...containerStyles,
                    marginTop: isMobile ? "6%" : "4%",
                    overflow: "hidden",
                    maxHeight: "500px",
                    overflowX: "hidden",
                    overflowY: "scroll",
                    marginBottom: "10px",
                }}
            >
                {showLoader && <Loading />}
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        flexDirection: "column",
                        padding: isMobile ? "20px 10px" : "5px 20px",
                    }}
                >
                    {isMobile && (
                        <h4
                            style={{
                                fontFamily: "var(--font-family-main)",
                                fontSize: "var(--font-size-large)",
                                margin: "0px 0 12px 0",
                            }}
                        >
                            Emi Calculator
                        </h4>
                    )}
                    <Box
                        sx={{
                            ...cardContainerStyles,
                            boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
                            width: "100%",
                            borderRadius: "10px",
                            justifyContent: "flex-start",
                            alignItems: "flex-start",
                            height: "auto",
                            paddingBottom: "50px",
                            marginBottom: "50px",
                            padding: "20px 20px 20px 20px",
                        }}
                    >
                        {/* Input & Dropdown */}
                        <Box sx={{ display: "flex", gap: 1, mb: 3, width: '100%' }}>
                            <Grid container spacing={isMobile ? 2 : 3} sx={{ margin: 0 }}>
                                <Grid item xs={12} sm={3.5}>
                                    <InputText
                                        title="Amount"
                                        value={principal === 0 ? "" : principal}
                                        onChange={(e) => setPrincipal(Number(e.target.value))}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3.5}>
                                    <InputText
                                        title="Percentage"
                                        value={interest === 0 ? "" : interest}
                                        onChange={(e) => setInterest(Number(e.target.value))}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3.5}>
                                    <InputText
                                        title="In Months"
                                        value={tenure === 0 ? "" : tenure}
                                        onChange={(e) => setTenure(Number(e.target.value))}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={1.5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                                    <Box>
                                        <StyledSubmitButton
                                            onClick={calculateEMI}
                                            themeMode={themeMUI.palette.mode}
                                            isMobile={isMobile}
                                        >
                                            Submit
                                        </StyledSubmitButton>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                        {/* Results */}
                        <Box sx={{ mt: 3 }}>
                            <Paper sx={{ ...cardContainerStyles, p: 2, mb: 1 }}>
                                <Typography >
                                    <span
                                        style={{
                                            ...cardContainerStyles,
                                            fontFamily: "var(--font-family-main)",
                                            fontSize: "var(--font-size-medium)"
                                        }}
                                    >
                                        Monthly Payment:{" "} <span style={{ fontWeight: 600 }}>{fixedFloat(emi)}</span>
                                    </span>
                                </Typography>
                            </Paper>
                            <Paper sx={{ ...cardContainerStyles, p: 2, mb: 1 }}>
                                <Typography >
                                    <span
                                        style={{
                                            ...cardContainerStyles,
                                            fontFamily: "var(--font-family-main)",
                                            fontSize: "var(--font-size-medium)"
                                        }}
                                    >
                                        Total Payment:{" "}<span style={{ fontWeight: 600 }}>{fixedFloat(total)}</span>
                                    </span>
                                </Typography>
                            </Paper>
                            <Paper sx={{ ...cardContainerStyles, p: 2, mb: 1 }}>
                                <Typography >
                                    <span
                                        style={{
                                            ...cardContainerStyles,
                                            fontFamily: "var(--font-family-main)",
                                            fontSize: "var(--font-size-medium)"
                                        }}
                                    >
                                        Total Interest Payable:{" "} <span style={{ fontWeight: 600 }}>{fixedFloat(intPayable)}</span>
                                    </span>
                                </Typography>
                            </Paper>
                        </Box>
                        {/* {total !== 0 && (
                            <Box
                                sx={{
                                    mt: 3,
                                    p: 2,
                                    borderRadius: 2,
                                    boxShadow: 2,
                                    backgroundColor: "#F7F9F9",
                                }}
                            >
                                <Typography
                                    sx={{ fontWeight: "bold", textAlign: "center", mb: 2 }}
                                    fontSize={theme.fonts.sm}
                                >
                                    Break-up of Total Payment
                                </Typography>
                                <EmiCustomPie
                                    data={send}
                                    totalCount={total}
                                    pieColors={["#292744", "#76D7C4"]}
                                    pieRadius={0}
                                />
                            </Box>
                        )} */}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

const mapStateToProps = (state: any) => {
    return {
        theme: state.theme.isLightMode,
    };
};
export default connect(mapStateToProps)(EmiCalculator);
